<template>
  <div>
    <b-form-group
        :state="validatedField && validatedField.isValid"
    >
      <span :style="dynamicStyle" v-if="!onlyInputVisible">
        <img  v-if="canTranslate && showFlag" :src="'https://silkplm.com/wp-content/uploads/flags/' + typingLanguage + '.webp'" style="width: 15px; height: 12px">
        <b-button v-if="canTranslate" variant="plm-action" :data-test-path="'translate-' + fieldLabel" @click="translateFromDef" size="sm"
                  style="width: 22px;
                  height: 18px;
                  padding-left: 1px;
                  padding-top: 0px;">
          <font-awesome-icon icon="language" transform="shrink-3 up-2"></font-awesome-icon>
        </b-button>

          <slot name="label">
          <span class="d-inline-flex align-items-center gap-2">
              {{ fieldLabel }}
              <HintEditor
                  v-if="fieldConfig"
                  ref="hint"
                  :editor-mode="editorMode"
                  :field-config="fieldConfig"
                  :help-message="fieldConfig.htmlHelpText"
                  :help-title="fieldConfig.helpWindowTitle"
                  :validated-field="validatedField"
              />
              <small v-if="validatedField && labelTip" class="form-text text-warning font-weight-bold pl-2">
              {{ labelTip || '&nbsp;' }}
              </small>
            </span>
          </slot>

        <span v-if="hasLink" class="pl-0">
              <b-link @click="goTo()" class="pr-1 pl-1" style="height: 16px;">
            <font-awesome-icon icon="link-horizontal" transform="shrink-4"></font-awesome-icon>
          </b-link>
        </span>
        <span v-if="fieldConfig.readOnly" class="text-primary">
          <font-awesome-icon icon="lock" style="height: 12px; padding-bottom: 1px;"></font-awesome-icon>
        </span>
        <span v-if="extraTip" class="text-primary" @click="showTooltip">
          <font-awesome-icon icon="circle-question"
                             style="height: 16px; color: orange;"></font-awesome-icon>
        </span>
      </span>
      <b-container>
        <b-row>
          <b-col class="p-0">
            <div class="input-group">
              <slot name="prepend"></slot>
              <div class="form-control grey-label readonly-input"
                   v-if="(readOnly || (!isStatic && (!validatedField || validatedField.readOnly))) && $slots['readOnly']">
                <slot name="readOnly"></slot>
              </div>
              <slot v-else name="editor"></slot>
            </div>
          </b-col>
        </b-row>

        <b-row v-if="!onlyInputVisible" style="padding-top: 3px">
          <b-col class="p-0">
            <small
                v-if="showValidateStatus && (validatedField && validatedField.isValid) && !validatedField.hintMessage && fieldConfig.shortHelpText"
                style="margin-top: 2px"
                class="form-text text-muted"> {{ fieldConfig.shortHelpText }}
            </small>
            <small v-else-if="showValidateStatus && !(validatedField && validatedField.isValid)"
                   style="margin-top: 2px; margin-left: 10px;"
                   class="form-text text-danger font-weight-bold">{{
                validatedField ? validatedField.errorMessage || '&nbsp;' : ' '
              }}
            </small>

            <small v-if="validatedField && validatedField.hintMessage" style="margin-top: 2px; margin-left: 10px;"
                   class="form-text text-warning font-weight-bold">{{ validatedField.hintMessage || '&nbsp;' }}
            </small>
            <small
                v-else-if="mode !== 'NEW' && hasChangedValue && (interfaceTranslationsEnabled || customerTranslationsEnabled) && validatedField.isValid"
                class="form-text text-warning font-weight-bold">{{ translationLabelInfo || '&nbsp;' }}
            </small>
          </b-col>
          <b-col style="height: 28px;" class="pt-1 pl-0 pr-0 col-auto">
            <transition name="fade">shareAtt
              <b-spinner
                  v-if="validatedField && validatedField.validationInProgress"
                  variant="plm-primary"
                  small
                  label="Spinning">
              </b-spinner>
            </transition>
          </b-col>
        </b-row>
      </b-container>
    </b-form-group>
  </div>
</template>

<script>
import ValidatedField from "@/validation/ValidatedField";
import FormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormFieldConfig";
import HintEditor from "@/components/inputs/controls/HintEditor";
import FormsActions from "@/components/forms/FormsActions";
import TextReadOnly from "@/components/forms/renderers/TextReadOnly";
import {
  findFieldValueFromCompanyCompanyConfigurationFormField
} from "@/components/forms/customizations/plm/EntityEditFormService";
import RegionSmIcon from "@/components/RegionSmIcon.vue";
import {
  detectDBLanguage,
  detectInputTypingLanguage,
  getValueForTypingInput, isCustomerTranslationEnabledOnField,
  isInterfaceTranslationEnabledOnField,
  prepareDialogInput, saveFieldsTranslationsFromDialog, showTranslationActivity
} from "@/utils/TranslationDispatcher";
import {cloneClassInstance, cloneEntity, stringifyForCompare} from "@/utils/Objects";
import Rpc from "@/Rpc";
import DecimalFormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/DecimalFormFieldConfig";
import DialogManager from "@/utils/DialogManager";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import Product from "@/server/com/silkplm/model/plm/Product";
import OrderDetails from "@/server/com/silkplm/model/plm/OrderDetails";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";

export default {
  name: "InputBase",
  components: {RegionSmIcon, HintEditor, TextReadOnly},

  async mounted() {
    this.assignLabelTip();
    this.assignExtraTip();
    this.refreshTranslation();
    if (this.openFromGrid) {
      this.savedDbValueToCompare = cloneEntity(this.validatedField.value);
    } else {
      this.validatedField.validatedForm.onLoaded(() => {
        this.savedDbValueToCompare = cloneEntity(this.validatedField.value);
      });
    }
  },

  methods: {
    forceSetValidate(isValid) {
      this.showValidateStatus = isValid;
    },

    refreshTranslation() {
      this.interfaceTranslationsEnabled = isInterfaceTranslationEnabledOnField(this.validatedField);
      this.customerTranslationsEnabled = isCustomerTranslationEnabledOnField(this.validatedField);
      this.canTranslate = this.customerTranslationsEnabled || this.interfaceTranslationsEnabled;

      this.typingLanguage = detectInputTypingLanguage(this.validatedField);
      this.fieldLanguageDb = detectDBLanguage(this.validatedField);
    },
    goTo() {
      if (this.hasLink) {
        let link = this.validatedField.validatedForm.originalSource.__computed_links__[this.fieldConfig.fieldName];
        if (link.clazz === Product.getRemoteClass()) {
          if (!SessionManager.hasFlag(FeatureFlag.SEPARATE_PRODUCT)) {
            let formClass = this.validatedField.validatedForm.formConfig.formClassLong;
            if (formClass === OrderDetails.getRemoteClass()) {
              link.id = this.validatedField.validatedForm.fields.productInSeasonId.value;
              link.clazz = ProductInSeason.getRemoteClass();
            }
          }
        }
        FormsActions.goTo(link);
      }
    },
    refreshLinks() {
      this.hasLink = false;
      if (this.fieldConfig?.joined) {
        if (this.validatedField?.validatedForm?.originalSource?.__computed_links__ &&
            this.validatedField.validatedForm.originalSource.__computed_links__[this.fieldConfig.fieldName]) {
          let link = this.validatedField.validatedForm.originalSource.__computed_links__[this.fieldConfig.fieldName];

          this.hasLink = true;
          this.link = link;
        }
      }
    },
    afterChangeVal(val) {
      let ignoredFields = new Set();
      ignoredFields.add("EID");
      this.hasChangedValue = !(stringifyForCompare(val, ignoredFields) === stringifyForCompare(this.savedDbValueToCompare, ignoredFields));
    },

    assignLabelTip() {
      let fieldConfig = this.fieldConfig;
      let validatedField = this.validatedField;
      if (validatedField?.validatedForm?.formConfig) {
        this.labelTip = findFieldValueFromCompanyCompanyConfigurationFormField(validatedField.validatedForm.formConfig, fieldConfig, "labelTip");
      }
    },

    async showTooltip() {
      await DialogManager.infoShort(this.fieldLabel + ' ' + $tr('extra.tip.title', 'additional tip'), this.extraTip);
    },

    assignExtraTip() {
      let fieldConfig = this.fieldConfig;
      let validatedField = this.validatedField;
      if (validatedField?.validatedForm?.formConfig) {
        this.extraTip = findFieldValueFromCompanyCompanyConfigurationFormField(validatedField.validatedForm.formConfig, fieldConfig, "extraTip");
        this.extraTip = this.extraTip?.replaceAll("\n", '<br />');
      }
    },

    async handleOpenFromGrid() {
      let result = await showTranslationActivity(prepareDialogInput(this.validatedField, this.openFromGrid));
      await saveFieldsTranslationsFromDialog(result, this.validatedField);
    },

    async handleOpenFromForm() {
      let result = await showTranslationActivity(prepareDialogInput(this.validatedField, this.openFromGrid));
      if (!result) {
        return;
      }
      if (!result["translations"] || this.mode !== 'EDIT') {
        return;
      }
      let translation = await saveFieldsTranslationsFromDialog(result, this.validatedField);
      this.validatedField.value = getValueForTypingInput(translation, this.validatedField);
    },

    async translateFromDef() {
      if (this.gridEditor) {
        this.gridEditor.close()
      }
      if (this.openFromGrid) {
        await this.handleOpenFromGrid();
      } else {
        await this.handleOpenFromForm();
      }
    },
  },

  model: {
    prop: 'inputValue',
    event: 'model-change',
  },

  props: {
    showFlag: {default: true},
    readOnly: {type: Boolean, default: false},
    editorMode: {type: Boolean, default: false},
    validatedField: {type: ValidatedField, default: null},
    fieldConfig: {type: FormFieldConfig, default: null},
    staticLabel: {default: null},
    isStatic: {default: false},
    openFromGrid: {default: false},
    gridEditor: {default: null},
    mode: {default: null},
    withSelect: {type: Boolean, default: false},
  },

  watch: {
    "validatedField": function (val) {
      this.onlyInputVisible = val?.__hideHints;
      this.refreshLinks();
      this.refreshTranslation();
      this.$emit('input', this.validatedField?.value);
    },

    "validatedField.value": function (val) {
      if (this.fieldConfig.fieldName === "retailPriceInitial") {
        let x = 5;
      }
      this.refreshLinks();
      this.$emit('input', val);
      this.$parent.$emit('input', val);
      if (this.savedDbValue === undefined) {
        this.savedDbValue = cloneEntity(val);
      }
      this.hasChangedValue = !(JSON.stringify(val) === JSON.stringify(this.savedDbValue));
    },
    "fieldConfig.fieldLabel": function (val) {
      this.fieldLabel = val;
      this.$parent.$emit('input', val);
    },
    staticLabel: function (label) {
      this.fieldLabel = label;
    },
  },

  computed: {
    dynamicStyle() {
      return {
        display: 'inline-block',
        width: this.withSelect ? '139%' : '100%'
      };
    }
  },

  data: function () {
    let onlyInputVisible = this.validatedField?.__hideHints;
    return {
      onlyInputVisible,
      showValidateStatus: true,
      hasLink: false,
      fieldLabel: this.staticLabel || this.fieldConfig.fieldLabel,
      labelTip: null,
      extraTip: null,
      canTranslate: false,
      customerTranslationsEnabled: false,
      interfaceTranslationsEnabled: false,
      typingLanguage: null,
      fieldLanguageDb: null,
      translationLabelInfo: $tr("inputBase.translationLabelInfo",
          "Editing a value does not automatically update translations. Go to translations and " +
          "generate correct translations for the updated value."
      ),
      hasChangedValue: false,
    }
  }
}
</script>
<style scoped>

</style>